import React, { memo, useMemo, useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "./DocumentUploadCalendar.css";

const COLOR_SCALE = ["#e8e9f0", "#9fa3c7", "#6c72a8", "#31356e", "#1c1f4e"];

const getColorLevel = (count) => {
  if (count === 0) return 0;
  if (count <= 2) return 1;
  if (count <= 5) return 2;
  if (count <= 9) return 3;
  return 4;
};

const getColor = (count) => COLOR_SCALE[getColorLevel(count)];

const getOrdinalSuffix = (num) => {
  const suffixes = ["th", "st", "nd", "rd"];
  const v = num % 100;
  return num + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
};

const formatDate = (dateString) => {
  const [year, month, day] = dateString.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  const monthName = date.toLocaleString("en-US", { month: "long" });
  const dayNum = date.getDate();
  const yearNum = date.getFullYear();
  return `${monthName} ${getOrdinalSuffix(dayNum)}, ${yearNum}`;
};

const getMonthLabel = (date) => {
  return date.toLocaleString("en-US", { month: "short" });
};

const getYearLabel = (year) => {
  return year === "last" ? "the last year" : year;
};

function DocumentUploadCalendar({
  documents = [],
  loading = false,
  user = null,
}) {
  const [selectedYear, setSelectedYear] = useState("last");
  const [hoveredDay, setHoveredDay] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const [visibleWeeks, setVisibleWeeks] = useState(null);
  const [blockSize, setBlockSize] = useState(null);
  const [blockMargin, setBlockMargin] = useState(3);
  const hideTimeoutRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, []);

  // Responsive resize handler
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const width = window.innerWidth;
        const isMob = width < 768;
        setIsMobile(isMob);

        const availableWidth = containerRef.current.clientWidth;
        if (isMob) {
          const margin = 3;
          const minBlockSize = 10;
          const maxBlockSize = 14;

          // Calculate how many columns can fit
          const cols = Math.floor((availableWidth + margin) / (minBlockSize + margin));
          const numWeeks = Math.max(8, Math.min(cols, 20));

          setVisibleWeeks(numWeeks);

          // Solve for exact block size to fill space
          const exactBlockSize = (availableWidth - (numWeeks - 1) * margin) / numWeeks;
          setBlockSize(Math.min(exactBlockSize, maxBlockSize));
          setBlockMargin(margin);
        } else {
          // Desktop: show all weeks, let CSS handle sizing
          setVisibleWeeks(null);
          setBlockSize(null);
          setBlockMargin(3);
        }
      }
    };

    const observer = new ResizeObserver(handleResize);
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    handleResize();

    return () => observer.disconnect();
  }, []);

  // Filter documents based on admin access level
  const filteredDocuments = useMemo(() => {
    if (!user) return documents;

    const { role, adminaccessLevel, location } = user;

    // System admin sees all documents
    if (role === "system_admin") return documents;

    return documents.filter((doc) => {
      const docProvince = String(doc.location?.province || doc.province || "")
        .trim()
        .toLowerCase();
      const docMunicipality = String(
        doc.location?.municipality || doc.municipality || "",
      )
        .trim()
        .toLowerCase();
      const docBarangay = String(doc.location?.barangay || doc.barangay || "")
        .trim()
        .toLowerCase()
        .replace(/^barangay\s+/i, "");

      const userProvince = String(location?.province || "")
        .trim()
        .toLowerCase();
      const userMunicipality = String(location?.municipality || "")
        .trim()
        .toLowerCase();
      const userBarangay = String(location?.barangay || "")
        .trim()
        .toLowerCase()
        .replace(/^barangay\s+/i, "");

      if (adminaccessLevel === "provincial") {
        // Provincial admin: sees province + all municipalities + all barangays in that province
        return docProvince === userProvince;
      } else if (adminaccessLevel === "municipal") {
        // Municipal admin: sees their municipality + barangays in that municipality
        return (
          docMunicipality === userMunicipality && docProvince === userProvince
        );
      } else if (adminaccessLevel === "barangay") {
        // Barangay admin: sees only their barangay
        return (
          docBarangay === userBarangay &&
          docMunicipality === userMunicipality &&
          docProvince === userProvince
        );
      }

      return false;
    });
  }, [documents, user]);

  // Calculate available years from filtered document data
  const availableYears = useMemo(() => {
    const years = new Set();

    filteredDocuments.forEach((doc) => {
      let uploadDate;

      if (doc.uploadedAt?.seconds) {
        uploadDate = new Date(doc.uploadedAt.seconds * 1000);
      } else if (doc.uploadedAt) {
        uploadDate = new Date(doc.uploadedAt);
      } else if (doc.createdAt) {
        uploadDate = new Date(doc.createdAt);
      } else {
        return;
      }

      years.add(uploadDate.getFullYear());
    });

    // Sort descending (newest first)
    return Array.from(years).sort((a, b) => b - a);
  }, [filteredDocuments]);

  // Generate calendar data based on selected year
  const calendarData = useMemo(() => {
    const today = new Date();
    const days = [];

    // Create a map of date -> document count
    const uploadCounts = {};

    filteredDocuments.forEach((doc) => {
      let uploadDate;

      // Handle Firestore timestamp or ISO string
      if (doc.uploadedAt?.seconds) {
        uploadDate = new Date(doc.uploadedAt.seconds * 1000);
      } else if (doc.uploadedAt) {
        uploadDate = new Date(doc.uploadedAt);
      } else if (doc.createdAt) {
        uploadDate = new Date(doc.createdAt);
      } else {
        return;
      }

      const dateKey = `${uploadDate.getFullYear()}-${String(uploadDate.getMonth() + 1).padStart(2, "0")}-${String(uploadDate.getDate()).padStart(2, "0")}`;
      uploadCounts[dateKey] = (uploadCounts[dateKey] || 0) + 1;
    });

    let startDate, endDate;

    if (selectedYear === "last") {
      endDate = today;
      startDate = new Date(today);
      startDate.setDate(startDate.getDate() - 364);
    } else {
      startDate = new Date(parseInt(selectedYear), 0, 1);
      endDate = new Date(parseInt(selectedYear), 11, 31);
    }

    const current = new Date(startDate);
    while (current <= endDate) {
      const dateKey = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, "0")}-${String(current.getDate()).padStart(2, "0")}`;

      days.push({
        date: dateKey,
        count: uploadCounts[dateKey] || 0,
        dayOfWeek: current.getDay(),
        month: current.getMonth(),
      });

      current.setDate(current.getDate() + 1);
    }

    return days;
  }, [filteredDocuments, selectedYear]);

  // Calculate total uploads in the period
  const totalUploads = useMemo(() => {
    return calendarData.reduce((sum, day) => sum + day.count, 0);
  }, [calendarData]);

  // Group by weeks for grid layout
  const weeks = useMemo(() => {
    const result = [];
    let currentWeek = [];

    // Fill in empty days at the start to align with Sunday
    const firstDay = calendarData[0]?.dayOfWeek || 0;
    for (let i = 0; i < firstDay; i++) {
      currentWeek.push(null);
    }

    calendarData.forEach((day) => {
      currentWeek.push(day);
      if (currentWeek.length === 7) {
        result.push(currentWeek);
        currentWeek = [];
      }
    });

    // Fill in remaining days
    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) {
        currentWeek.push(null);
      }
      result.push(currentWeek);
    }

    // On mobile, slice to show only visible weeks (starting from the end - most recent)
    if (isMobile && visibleWeeks && result.length > visibleWeeks) {
      // Align to start on Sunday by finding the first Sunday in the slice range
      let startIndex = result.length - visibleWeeks;
      while (startIndex < result.length) {
        const firstDayOfWeek = result[startIndex]?.find((d) => d !== null);
        if (firstDayOfWeek) {
          const [year, monthStr, dayStr] = firstDayOfWeek.date.split("-").map(Number);
          const date = new Date(year, monthStr - 1, dayStr);
          if (date.getDay() === 0) break;
        }
        startIndex++;
      }
      return result.slice(startIndex);
    }

    return result;
  }, [calendarData, isMobile, visibleWeeks]);

  const handleDayHover = (day, e) => {
    if (!day) return;

    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    const rect = e.target.getBoundingClientRect();

    const tooltipWidth = 120;

    setTooltipPos({
      x: rect.left + rect.width / 2 - tooltipWidth / 2,
      y: rect.top - 55, // <-- Increase this number (e.g., to 60 or 65) to make the gap larger
    });
    setHoveredDay(day);
  };

  const handleDayLeave = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setHoveredDay(null);
    }, 150); // Small delay to prevent hiding when crossing gaps
  };

  const monthLabels = useMemo(() => {
    const labels = [];
    let currentLastLabelWeek = -10;
    let currentMonth = -1;

    weeks.forEach((week, weekIndex) => {
      const firstDayOfWeek = week.find((d) => d !== null);
      if (firstDayOfWeek) {
        const [year, monthStr] = firstDayOfWeek.date.split("-").map(Number);
        const date = new Date(year, monthStr - 1, 1);
        const month = date.getMonth();

        if (month !== currentMonth) {
          if (weekIndex - currentLastLabelWeek > 2) {
            currentMonth = month;
            currentLastLabelWeek = weekIndex;
            labels.push({
              weekIndex,
              label: getMonthLabel(date),
            });
          }
        }
      }
    });

    return labels;
  }, [weeks]);

  if (loading) {
    return (
      <div className="chart-card document-calendar-wrapper">
        <div className="chart-header-row">
          <div className="chart-titles">
            <h2 className="chart-title">Document Uploads</h2>
            <p className="chart-subtitle">Daily document upload activity</p>
          </div>
          <div className="filter-row">
            <button
              onClick={() => setSelectedYear("last")}
              className={`filter-select ${selectedYear === "last" ? "active" : ""}`}
            >
              Last Year
            </button>
            {availableYears.map((year) => (
              <button
                key={year}
                onClick={() => setSelectedYear(year)}
                className={`filter-select ${selectedYear === year ? "active" : ""}`}
              >
                {year}
              </button>
            ))}
          </div>
        </div>
        <div className="calendar-loading">Loading upload data...</div>
      </div>
    );
  }

  return (
    <div className="chart-card document-calendar-wrapper">
      <div className="chart-header-row">
        <div className="chart-titles">
          <h2 className="chart-title">Document Uploads</h2>
          <p className="chart-subtitle">Daily document upload activity</p>
        </div>
        <div className="filter-row">
          <button
            onClick={() => setSelectedYear("last")}
            className={`filter-select ${selectedYear === "last" ? "active" : ""}`}
          >
            Last Year
          </button>
          {availableYears.map((year) => (
            <button
              key={year}
              onClick={() => setSelectedYear(year)}
              className={`filter-select ${selectedYear === year ? "active" : ""}`}
            >
              {year}
            </button>
          ))}
        </div>
      </div>

      <div ref={containerRef} key={selectedYear} className="calendar-wrapper">
        {/* Month labels */}
        <div className={`calendar-months ${isMobile ? 'mobile' : ''}`}>
          {monthLabels.map((m, i) => (
            <div
              key={i}
              className="month-label"
              style={{ left: `${(m.weekIndex / weeks.length) * 100}%` }}
            >
              {m.label}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div
          className="calendar-grid"
          style={{
            gap: isMobile ? `${blockMargin}px` : '3px',
          }}
        >
          {weeks.map((week, weekIndex) => (
            <div key={weekIndex} className="calendar-week">
              {week.map((day, dayIndex) => (
                <div
                  key={dayIndex}
                  className={`calendar-day ${day ? "has-data" : "empty"}`}
                  style={{
                    backgroundColor: day ? getColor(day.count) : "transparent",
                    width: isMobile && blockSize ? `${blockSize}px` : '100%',
                    minWidth: isMobile && blockSize ? `${blockSize}px` : '10px',
                  }}
                  onMouseEnter={(e) => handleDayHover(day, e)}
                  onMouseLeave={handleDayLeave}
                />
              ))}
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className="calendar-footer">
          <span className="calendar-total">
            {totalUploads.toLocaleString()} documents uploaded in{" "}
            {isMobile && visibleWeeks ? `last ${visibleWeeks * 7} days` : getYearLabel(selectedYear)}
          </span>
          <div className="calendar-legend">
            <span className="legend-label">Less</span>
            <div className="legend-scale">
              {COLOR_SCALE.map((color, i) => (
                <div
                  key={i}
                  className="legend-block"
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
            <span className="legend-label">More</span>
          </div>
        </div>
      </div>

      {/* Custom Tooltip - Chart.js style with smooth animation */}
      <motion.div
        className="calendar-tooltip"
        initial={{ opacity: 0, scale: 0.9, x: 0, y: 0 }}
        animate={{
          opacity: hoveredDay ? 1 : 0,
          scale: hoveredDay ? 1 : 0.9,
          x: tooltipPos.x,
          y: tooltipPos.y,
        }}
        transition={{
          opacity: { duration: 0.15 },
          scale: { duration: 0.15 },
          x: { type: "spring", stiffness: 300, damping: 25 },
          y: { type: "spring", stiffness: 300, damping: 25 },
        }}
        style={{
          position: "fixed",
          left: 0,
          top: 0,
          pointerEvents: "none",
          zIndex: 1000,
        }}
      >
        <div className="tooltip-content">
          {hoveredDay && (
            <>
              <span className="tooltip-count">
                {hoveredDay.count}{" "}
                {hoveredDay.count === 1 ? "document" : "documents"}
              </span>
              <span className="tooltip-date">
                {formatDate(hoveredDay.date)}
              </span>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}

export default memo(DocumentUploadCalendar);
